import React, { useRef, useEffect, useState } from 'react';
import NodeTooltip from './NodeTooltip';

export interface NodeLabelProps {
  /** The label text to display */
  label: string;
  /**
   * @deprecated No longer used. Labels now wrap across up to 2 lines and
   * only truncate (with ellipsis + tooltip) when they still don't fit.
   */
  maxLength?: number;
  /** Whether to show a tooltip on truncation (default: true) */
  showTooltip?: boolean;
  /** Font weight class */
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  /** Text color class */
  textColor?: string;
  /** Text size class */
  textSize?: 'xs' | 'sm' | 'base' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Test ID for testing */
  testId?: string;
}

const fontWeightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const textSizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Label component that wraps across up to 2 lines and shows a tooltip when
 * the text is still truncated.
 */
const NodeLabel: React.FC<NodeLabelProps> = ({
  label,
  showTooltip = true,
  fontWeight = 'medium',
  textColor = 'text-gray-900 dark:text-warm-white',
  textSize = 'base',
  className = '',
  testId,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Detect CSS overflow after line-clamp wraps to 2 lines
  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const el = textRef.current;
        setIsTruncated(el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [label]);

  // Wrap across up to 2 lines, then ellipsize. Uses inline styles because
  // the project uses Tailwind 3.2 which doesn't ship line-clamp by default.
  const labelElement = (
    <span
      ref={textRef}
      className={`break-words overflow-hidden ${fontWeightClasses[fontWeight]} ${textColor} ${textSizeClasses[textSize]} ${className}`}
      style={{
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
      }}
      data-testid={testId}
    >
      {label}
    </span>
  );

  // Wrap with tooltip if truncated and tooltip is enabled
  if (showTooltip && isTruncated) {
    return (
      <NodeTooltip content={label} side="top">
        {labelElement}
      </NodeTooltip>
    );
  }

  return labelElement;
};

export default NodeLabel;
